import { Outlet, Link, createRootRoute, useRouter, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { clearOnIdentityChange } from "../api/queryKeys";
import { applyTheme, useUiStore } from "../store/uiStore";
import { Toaster } from "../components/ui/sonner";
import { AppShell } from "../shell/AppShell";
import { SessionGate } from "../shell/SessionGate";
import { PermissionRouteGuard } from "../shell/PermissionRouteGuard";
import { retainScopeId } from "./urlState";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-lg">
      <div className="max-w-[28rem] text-center">
        <h1 className="type-title text-foreground">404</h1>
        <p className="type-body mt-sm text-muted-foreground">This page does not exist.</p>
        <div className="mt-lg">
          <Link
            to="/"
            className="type-body inline-flex h-control-md items-center justify-center rounded-md bg-primary px-md font-medium text-primary-foreground"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-lg">
      <div className="max-w-[28rem] text-center">
        <h1 className="type-section text-foreground">This page didn't load</h1>
        <p className="type-body mt-sm text-muted-foreground">
          Something went wrong. Try again, or head back home.
        </p>
        <div className="mt-lg flex flex-wrap justify-center gap-sm">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="type-body inline-flex h-control-md items-center justify-center rounded-md bg-primary px-md font-medium text-primary-foreground"
          >
            Try again
          </button>
          <a
            href="/"
            className="type-body inline-flex h-control-md items-center justify-center rounded-md border border-input bg-background px-md font-medium text-foreground"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

// A plain root route (decision F-D4). It used to carry `head()` and a `shellComponent` that
// rendered `<html>`/`<head>`/`<body>` for TanStack Start's SSR; with no server there is nothing
// to render a document on the fly, so the document is `index.html` and the meta tags, font
// links and pre-paint theme script live there. What stays here is what is genuinely the
// router's: the query client, the theme effect, and the shell frame below.
export const Route = createRootRoute({
  /* The selected planning scope rides every router-built link (`scopeId`, PS-D12). The cast is
     the middleware's search type: the app declares no search schema, `urlState.ts` owns the keys. */
  search: { middlewares: [retainScopeId as never] },
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootComponent() {
  const theme = useUiStore((s) => s.theme);
  // One client per mount. It was "one per request" when this rendered on a server, where a
  // module-level singleton would have leaked cache between users; in the browser it is simply
  // the client's lifetime, and `useState` keeps it stable across re-renders.
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: { queries: { staleTime: 30_000, retry: 1 } },
      }),
  );

  // The cache is cleared on every identity change; no caller decides this.
  useEffect(() => clearOnIdentityChange(queryClient), [queryClient]);

  useEffect(() => {
    applyTheme(theme);
    if (theme !== "system") return;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyTheme("system");
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [theme]);

  return (
    <QueryClientProvider client={queryClient}>
      <ShellFrame />
      {/* Mounted once; `useCommand` is its only writer. */}
      <Toaster position="bottom-right" />
    </QueryClientProvider>
  );
}

/**
 * Every route renders inside the shell, except the three that come before it: sign-in, the
 * identity provider's callback, and the tenant picker. None can render inside `AppShell`,
 * because the shell's navigation is built from a bootstrap call that needs a tenant -- which is
 * the thing the last two exist to choose.
 */
const UNSHELLED = new Set(["/login", "/auth/callback", "/select-tenant"]);

function ShellFrame() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  if (UNSHELLED.has(pathname)) return <Outlet />;
  // The guard chain, outermost first: session, then the route's permission, then (at the page)
  // the module gate.
  return (
    <SessionGate>
      <AppShell>
        <PermissionRouteGuard>
          <Outlet />
        </PermissionRouteGuard>
      </AppShell>
    </SessionGate>
  );
}
